import type { Story } from '@/store/story-types'

export const FASHION_CREATOR_STORE_STORY: Story = {
  id: 'fashion-creator-store',
  name: 'Fashion Creator Store',
  narrative:
    'A mobile-first creator commerce store showcasing fashion products with social proof, product reviews, and a seamless shopping cart experience.',
  goals: [
    'Showcase creator brand and featured products',
    'Build trust through social proof and reviews',
    'Enable product browsing and detail viewing',
    'Provide smooth cart-to-checkout flow',
    'Confirm orders with clear confirmation page',
  ],
  lifecycle: 'building',
  pages: [
    {
      id: 'pg-home',
      title: 'Home',
      order: 0,
      status: 'draft',
      elementCount: 4,
      linkedPageId: 'home',
      description:
        'Landing page with creator hero, featured products grid, and social proof section',
      blockRef: { category: 'store', blockId: 'creator-hero' },
    },
    {
      id: 'pg-product-detail',
      title: 'Product Detail',
      order: 1,
      status: 'draft',
      elementCount: 3,
      linkedPageId: 'product-detail',
      description:
        'Product detail view with images, reviews, and related products',
      blockRef: { category: 'store', blockId: 'product-detail' },
    },
    {
      id: 'pg-cart',
      title: 'Cart',
      order: 2,
      status: 'draft',
      elementCount: 1,
      linkedPageId: 'cart',
      description: 'Shopping cart with item list, quantities, and total',
      blockRef: { category: 'store', blockId: 'cart-view' },
    },
    {
      id: 'pg-checkout',
      title: 'Checkout',
      order: 3,
      status: 'draft',
      elementCount: 1,
      linkedPageId: 'checkout',
      description:
        'Checkout form with shipping address, payment method, and order summary',
      blockRef: { category: 'store', blockId: 'checkout' },
    },
    {
      id: 'pg-order-confirmation',
      title: 'Order Confirmed',
      order: 4,
      status: 'draft',
      elementCount: 1,
      linkedPageId: 'order-confirmation',
      description: 'Order confirmation with summary and next steps',
      blockRef: { category: 'store', blockId: 'order-confirmation' },
    },
  ],
  bddScenarios: [
    {
      id: 'bdd-browse',
      name: 'Browse Featured Products',
      steps: [
        { type: 'given', text: 'the shopper is on the home page' },
        { type: 'when', text: 'the page loads' },
        {
          type: 'then',
          text: 'the creator hero banner, featured products grid, and social proof section are visible',
          linkedPageId: 'home',
        },
      ],
    },
    {
      id: 'bdd-view-product',
      name: 'View Product Detail',
      steps: [
        { type: 'given', text: 'the shopper is browsing the featured products' },
        {
          type: 'when',
          text: 'the shopper taps on a product card',
        },
        {
          type: 'then',
          text: 'the product detail page shows images, price, description, reviews, and related products',
          linkedPageId: 'product-detail',
        },
      ],
    },
    {
      id: 'bdd-add-to-cart',
      name: 'Add Product to Cart',
      steps: [
        {
          type: 'given',
          text: 'the shopper is viewing a product detail page',
        },
        { type: 'when', text: 'the shopper selects a size and taps "Add to Cart"' },
        {
          type: 'then',
          text: 'the product is added to the cart and the cart badge updates',
          linkedPageId: 'cart',
        },
      ],
    },
    {
      id: 'bdd-checkout',
      name: 'Complete Checkout',
      steps: [
        { type: 'given', text: 'the shopper has items in the cart' },
        { type: 'when', text: 'the shopper proceeds to checkout and fills in shipping and payment' },
        {
          type: 'then',
          text: 'the order is placed and the confirmation page is shown',
          linkedPageId: 'order-confirmation',
        },
      ],
    },
  ],
}
